import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import M1 from "../assets/ribbon/M1.jpg";
import Offer1 from "../assets/ribbon/Offer1.png";
import Offer2 from "../assets/ribbon/Offer2.png";
import hypermarket from "../assets/ribbon/hypermarket.jpg";
import loyalty from "../assets/ribbon/loyalty.jpg";
import W1 from "../assets/ribbon/W1.jpg";
import W2 from "../assets/ribbon/W2.jpg";

const homeRed = "#A40000";
const conveyrPurple = "#A40000";

export default function Hypermarket() {
  const scrollRibbonRef = useRef<HTMLDivElement>(null);
  const speedRef = useRef(1);
  const [isHoveredBottom, setIsHoveredBottom] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    let position = 0;
    let animationId: number;

    const animate = () => {
      if (scrollRibbonRef.current) {
        position -= speedRef.current;
        scrollRibbonRef.current.style.transform = `translateX(${position}px)`;
        const width = scrollRibbonRef.current.scrollWidth / 2;
        if (Math.abs(position) >= width) position = 0;
      }
      animationId = requestAnimationFrame(animate);
    };

    animate();

    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      const delta = Math.abs(window.scrollY - lastScrollY);
      speedRef.current = Math.min(6, 1 + delta * 0.15);
      lastScrollY = window.scrollY;
      setTimeout(() => (speedRef.current = 1), 150);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /* ================= TEXT CONTENT ================= */

  const middleTextContent = (
  <>
    <h2
      style={{
        color: isHoveredBottom ? "#fff" : homeRed,
        fontSize: "1.45rem",
        marginTop: 0,
        marginBottom: "1rem",
        transition: "color 0.35s ease",
      }}
    >
      Introduction
    </h2>

    <p style={{ transition: "color 0.35s ease", color: isHoveredBottom ? "#fff" : "inherit" }}>
      URBANGRO is a next-generation, technology-enabled retail brand built to transform the way urban India shops for everyday essentials and lifestyle products. Designed as a large-format, omni-channel retail platform, URBANGRO integrates physical stores with digital commerce to deliver a seamless, efficient, and customer-centric shopping experience.
    </p>

    <p style={{ transition: "color 0.35s ease", color: isHoveredBottom ? "#fff" : "inherit" }}>
      URBANGRO is part of the broader Conveyr ecosystem, enabling deep technology integration, logistics optimization, digital payments, loyalty programs, and AI-driven operations. While the long-term vision includes multi-category retail and lifestyle expansion, URBANGRO’s initial rollout is strategically focused on daily needs and kitchen essentials, ensuring high-frequency customer engagement, predictable demand, and strong early-stage unit economics.
    </p>

    <p style={{ transition: "color 0.35s ease", color: isHoveredBottom ? "#fff" : "inherit" }}>
      This phased approach allows URBANGRO to build a strong revenue foundation before expanding into a full hypermarket ecosystem.
    </p>

    <hr style={{ margin: "1.2rem 0", opacity: 0.3 }} />

    <h3>The Problem URBANGRO Solves</h3>

    <p>India’s retail market remains fragmented and operationally inefficient:</p>

    <ul style={{ marginLeft: "1.5rem" }}>
      <li>Small neighborhood stores lack assortment, pricing transparency, and technology</li>
      <li>Large supermarkets often provide limited depth in essential categories</li>
      <li>E-commerce platforms offer convenience but lack physical touchpoints for fresh and urgent purchases</li>
      <li>Customers face long billing queues, inconsistent availability, and disconnected online-offline experiences</li>
    </ul>

    <p>
      URBANGRO bridges this gap by combining the trust and immediacy of physical retail with the speed, intelligence, and personalization of digital commerce.
    </p>

    <hr style={{ margin: "1.2rem 0", opacity: 0.3 }} />

    <h3>What URBANGRO Is Building</h3>

    <p>
      URBANGRO is developing a 30,000 sq. ft. tech-enabled hypermarket format designed specifically for Indian consumer behavior, pricing sensitivity, local assortment patterns, and high-frequency shopping needs.
    </p>

    <p><strong>Phase 1 — Core Focus Categories</strong></p>

    <ul style={{ marginLeft: "1.5rem" }}>
      <li>Fresh fruits and vegetables</li>
      <li>Groceries and staples</li>
      <li>FMCG products</li>
      <li>Dairy, bakery, and beverages</li>
      <li>Cleaning and household supplies</li>
      <li>Electronics and appliances</li>
      <li>Fashion and apparel</li>
      <li>Home essentials and furniture</li>
      <li>Lifestyle and stationery</li>
      <li>Bakery and ready-to-eat foods</li>
      <li>Footwear, sports, and décor categories</li>
      <li>Kitchen essentials and daily-use products</li>
    </ul>

    <p>This category mix is optimized to deliver:</p>

    <ul style={{ marginLeft: "1.5rem" }}>
      <li>High footfall frequency</li>
      <li>Rapid inventory turnover</li>
      <li>Strong repeat purchases</li>
      <li>Stable and predictable revenue</li>
    </ul>

    <p>
      This phased expansion enables URBANGRO to evolve into a complete hypermarket ecosystem without compromising early-stage profitability.
    </p>

    <hr style={{ margin: "1.2rem 0", opacity: 0.3 }} />

    <h3>Omni-Channel Retail Model</h3>

    <p>URBANGRO operates as both:</p>

    <ol style={{ marginLeft: "1.5rem" }}>
      <li>A large-format physical retail store, and</li>
      <li>A full-scale digital commerce platform,</li>
    </ol>

    <p>both seamlessly integrated through Conveyr’s omni-channel architecture.</p>

    <p>Customers can:</p>

    <ul style={{ marginLeft: "1.5rem" }}>
      <li>Shop in-store</li>
      <li>Order online via app or web</li>
      <li>Choose home delivery</li>
      <li>Opt for click-and-collect pickup</li>
      <li>Use digital wallets and loyalty rewards</li>
      <li>Receive personalized offers and AI-driven recommendations</li>
    </ul>

    <p>
      This unified commerce model improves customer convenience, increases engagement, and drives long-term loyalty.
    </p>

    <hr style={{ margin: "1.2rem 0", opacity: 0.3 }} />

    <h3>URBANGRO’s Role in the Conveyr Ecosystem</h3>

    <p>URBANGRO is the first and foundational commerce module within the Conveyr ecosystem, supporting:</p>

    <ul style={{ marginLeft: "1.5rem" }}>
      <li>Conveyr E-commerce</li>
      <li>Conveyr Logistics & Fulfillment</li>
      <li>Conveyr Payments & Wallet</li>
      <li>Conveyr Loyalty & Rewards Engine</li>
      <li>Conveyr Data, AI & Predictive Retail Systems</li>
    </ul>

    <p>
      Each URBANGRO store acts as a physical distribution hub for the Conveyr digital platform — enabling faster deliveries, localized inventory fulfillment, and omnichannel scale.
    </p>

    <p>
      This creates a powerful long-term moat by tightly coupling physical infrastructure with digital intelligence.
    </p>

    <hr style={{ margin: "1.2rem 0", opacity: 0.3 }} />

    <h3>Technology-Driven Operations</h3>

    <p>Technology is at the core of URBANGRO’s business model.</p>

    <p><strong>Customer-Facing Technology</strong></p>
    <ul style={{ marginLeft: "1.5rem" }}>
      <li>QR-based Scan & Go checkout</li>
      <li>Smart cart integration</li>
      <li>Digital wallet and payments</li>
      <li>Loyalty and rewards programs</li>
      <li>Personalized AI recommendations</li>
      <li>Real-time promotions and offers</li>
    </ul>

    <p><strong>Backend & Supply Chain Technology</strong></p>
    <ul style={{ marginLeft: "1.5rem" }}>
      <li>Real-time inventory visibility</li>
      <li>Automated replenishment systems</li>
      <li>AI-based demand forecasting</li>
      <li>Data-driven pricing optimization</li>
      <li>Vendor analytics and performance tracking</li>
      <li>Loss prevention systems</li>
    </ul>

    <p><strong>Operational Impact</strong></p>
    <ul style={{ marginLeft: "1.5rem" }}>
      <li>Reduced billing queues</li>
      <li>Faster store throughput</li>
      <li>Improved basket size</li>
      <li>Lower stock-outs and wastage</li>
      <li>Higher margins and retention</li>
    </ul>

    <p>
      URBANGRO is built as a future-ready retail platform, not a traditional supermarket.
    </p>
  </>
);


  const bottomTextContent = (
  <>
    <h2
      style={{
        color: isHoveredBottom ? "#fff" : conveyrPurple,
        fontSize: "1.45rem",
        marginTop: 0,
        marginBottom: "1rem",
        transition: "color 0.35s ease",
      }}
    >
      Store Design & Customer Experience
    </h2>

    <p>
      URBANGRO stores are designed to maximize efficiency, comfort, and speed:
    </p>

    <ul style={{ marginLeft: "1.5rem" }}>
      <li>Wide aisles and intuitive category zoning</li>
      <li>Dedicated fresh produce zones</li>
      <li>Clear navigation signage</li>
      <li>Optimized checkout layouts</li>
      <li>Clean, modern interiors</li>
      <li>High-visibility merchandising displays</li>
    </ul>

    <p>
      The customer experience is designed around convenience, speed, and trust, especially for high-frequency daily shoppers.
    </p>

    <hr style={{ margin: "1.2rem 0", opacity: 0.3 }} />

    <h3>Business Model & Revenue Streams</h3>

    <p>URBANGRO generates revenue through:</p>

    <ul style={{ marginLeft: "1.5rem" }}>
      <li>In-store product sales</li>
      <li>Online order fulfillment</li>
      <li>Delivery and convenience fees</li>
      <li>Vendor-funded in-store activations</li>
      <li>Brand partnerships and promotions</li>
      <li>Loyalty-driven repeat purchases</li>
      <li>Future private-label product margins</li>
    </ul>

    <p>
      The essentials-led format ensures stable demand, predictable cash flows, and high capital efficiency.
    </p>

    <hr style={{ margin: "1.2rem 0", opacity: 0.3 }} />

    <h3>Supply Chain & Vendor Strategy</h3>

    <p>URBANGRO follows a vendor-optimized procurement model:</p>

    <ul style={{ marginLeft: "1.5rem" }}>
      <li>Strategic partnerships with manufacturers and distributors</li>
      <li>Aggregator sourcing for fresh produce</li>
      <li>Select consignment inventory programs</li>
      <li>Regional supplier collaborations</li>
      <li>Vendor-supported infrastructure (racks, refrigeration, displays)</li>
    </ul>

    <p>
      This model reduces inventory risk, improves product freshness, and strengthens supplier relationships.
    </p>

    <hr style={{ margin: "1.2rem 0", opacity: 0.3 }} />

    <h3>Market Opportunity</h3>

    <p>India’s retail economy is undergoing rapid transformation:</p>

    <ul style={{ marginLeft: "1.5rem" }}>
      <li>Expected to cross USD 2 trillion by 2032</li>
      <li>Driven by urbanization, income growth, and digital adoption</li>
    </ul>

    <p>
      Despite this growth, the market lacks tech-enabled, omni-channel, large-format essential retail platforms built for scale. URBANGRO is positioned to fill this gap.
    </p>

    <hr style={{ margin: "1.2rem 0", opacity: 0.3 }} />

    <h3>Current Progress & Execution Readiness</h3>

    <p>URBANGRO is pre-launch but operationally prepared:</p>

    <ul style={{ marginLeft: "1.5rem" }}>
      <li>Two flagship locations identified (25,000 sq. ft. and 30,000 sq. ft.)</li>
      <li>Prime catchment areas with 50,000+ households</li>
      <li>Builder term sheets secured with favorable rent-free periods</li>
      <li>Product assortment finalized</li>
      <li>Supplier onboarding in progress</li>
      <li>Technology integration blueprint completed</li>
      <li>Store layouts, SOPs, staffing plans, and customer journey maps ready</li>
    </ul>

    <p>
      URBANGRO is positioned to move into immediate execution post funding closure.
    </p>

    <hr style={{ margin: "1.2rem 0", opacity: 0.3 }} />

    <h3>Scalability & Expansion Strategy</h3>

    <p>URBANGRO is built as a scalable retail chain:</p>

    <p><strong>Phase 1 (Year 1–2)</strong></p>
    <ul style={{ marginLeft: "1.5rem" }}>
      <li>Launch first two flagship stores</li>
      <li>Expand to 5 stores across Tier 1 & Tier 2 cities</li>
    </ul>

    <p><strong>Phase 2 (Year 3–5)</strong></p>
    <ul style={{ marginLeft: "1.5rem" }}>
      <li>Scale to 20 stores nationally</li>
      <li>Introduce private-label brands</li>
    </ul>

    <p><strong>Phase 3</strong></p>
    <ul style={{ marginLeft: "1.5rem" }}>
      <li>Build regional distribution hubs</li>
      <li>Expand Conveyr commerce footprint</li>
      <li>Franchise and asset-light expansion</li>
    </ul>

    <p>
      Long-term, URBANGRO becomes the physical commerce backbone of the Conveyr ecosystem.
    </p>

    <hr style={{ margin: "1.2rem 0", opacity: 0.3 }} />

    <h3>Competitive Differentiation</h3>

    <p>URBANGRO differentiates itself by combining:</p>

    <ul style={{ marginLeft: "1.5rem" }}>
      <li>Large-format physical presence</li>
      <li>Omni-channel retail architecture</li>
      <li>Technology-first operations</li>
      <li>High-frequency essentials-led model</li>
      <li>AI-powered analytics and forecasting</li>
      <li>Integration with a broader digital ecosystem</li>
    </ul>

    <p>
      This creates a defensible competitive advantage that traditional retailers struggle to replicate.
    </p>

    <hr style={{ margin: "1.2rem 0", opacity: 0.3 }} />

    <h3>Brand Promise</h3>

    <p><em>“More Variety. More Value. More Experience.”</em></p>

    <p>URBANGRO delivers:</p>

    <ul style={{ marginLeft: "1.5rem" }}>
      <li>Reliable product availability</li>
      <li>Transparent pricing</li>
      <li>Fast and convenient shopping</li>
      <li>Technology-enabled service excellence</li>
      <li>Consistent quality and freshness</li>
    </ul>
  </>
);


  /* ================= RENDER ================= */

  return (
    <div style={{ background: "#f5f5f5", minHeight: "100vh" }}>
  <div
    style={{
      maxWidth: "1500px",
      margin: "0 auto",
      padding: "30px 20px",
    }}
  >
    {/* 🔹 TITLE ROW (HEADING LEFT, IMAGE RIGHT) */}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "0rem",
        width: "100%",
        marginBottom: "0rem",
        marginTop: "-10px",
      }}
    >
      <h1
        style={{
          color: homeRed,
          fontWeight: "bold",
          margin: 0,
        }}
      >
        URBANGRO Hypermarket
      </h1>

      <img
        src={hypermarket}
        alt="URBANGRO Hypermarket"
        style={{
          height: "90px",
          width: "auto",
          objectFit: "contain",
          marginTop: "10px",
        }}
      />
    </div>

    {/* 🔹 SUBTITLE */}
    <p style={{ marginBottom: "30px" }}>
      A technology-driven hypermarket chain built for modern urban India.
    </p>

        {/* CARD */}
        <div style={{ background: "#fff", padding: "30px", borderRadius: 10, boxShadow: "0 3px 10px rgba(0,0,0,0.15)" }}>

          {/* TWO TILE LAYOUT */}
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: "2rem",
              alignItems: "flex-start",
            }}
          >
            {/* LEFT */}
            <div style={{ maxWidth: "1000px", flex: 1, display: "flex", flexDirection: "column", gap: "1.5rem" }}>
              <div
                onMouseEnter={() => setIsHoveredBottom(true)}
                onMouseLeave={() => setIsHoveredBottom(false)}
                style={{
                  padding: "1.5rem 2rem",
                  borderRadius: "18px",
                  background: isHoveredBottom ? homeRed : "transparent",
                  color: isHoveredBottom ? "#fff" : homeRed,
                  transition: "all 0.35s ease",
                }}
              >
                {middleTextContent}
              </div>
              
            {/* ===== IMAGE RIBBON (FULL-WIDTH BETWEEN CONTENT) ===== */}
              <div
                style={{
                  maxWidth: "2500px",
                  width: "calc(100% + 26rem)",     // expand beyond inner padding
                  marginLeft: "0rem",            // cancel left padding
                  marginRight: "0rem",           // cancel right padding
                  overflow: "hidden",
                  marginTop: "5rem",
                  marginBottom: "5rem",
                  padding: "1rem 0",
                  background: "linear-gradient(90deg, #f5f5f5, #ffffff, #f5f5f5)",
                  borderTop: "1px solid rgba(0,0,0,0.08)",
                  borderBottom: "1px solid rgba(0,0,0,0.08)",
                }}
              >
                <div
                  ref={scrollRibbonRef}
                  style={{
                    display: "flex",
                    gap: "60px",
                    alignItems: "center",
                    whiteSpace: "nowrap",
                    willChange: "transform",
                  }}
                >
                  {[...Array(2)].map((_, i) => (
                    <React.Fragment key={i}>
                      <img src={hypermarket} height={70} alt="" />
                      <img src={Offer1} height={70} alt="" />
                      <img src={M1} height={70} alt="" />
                      <img src={loyalty} height={70} alt="" />
                      <img src={Offer2} height={70} alt="" />
                      <img src={W1} height={70} alt="" />
                      <img src={W2} height={70} alt="" />
                    </React.Fragment>
                  ))}
                </div>
              </div>
  

              <div
                onMouseEnter={() => setIsHoveredBottom(true)}
                onMouseLeave={() => setIsHoveredBottom(false)}
                style={{
                  width: "100%",
                  maxWidth: "1000px",
                  padding: "1.5rem 2rem",
                  borderRadius: "18px",
                  background: isHoveredBottom ? conveyrPurple : "transparent",
                  color: isHoveredBottom ? "#fff" : conveyrPurple,
                  transition: "all 0.35s ease",
                }}
              >
                {bottomTextContent}
                <Link to="/product" style={{ display: "inline-block", marginTop: "1rem", color: "inherit", fontWeight: "bold" }}>
                  Product →
                </Link>
              </div>
            </div>

            {/* RIGHT IMAGES */}
            <div
              style={{
                flex: isMobile ? "none" : "0 0 28%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "15rem",
                marginTop: isMobile ? "50rem" : "10",
              }}
            >
              <img src={require("../assets/ribbon/W2.jpg")} alt="Conveyr logo" style={{ maxWidth: "280px" }} />
              <img src={require("../assets/ribbon/delivery.jpg")} alt="Conveyr logo" style={{ maxWidth: "280px" }} />
              <img src={require("../assets/ribbon/W1.jpg")} alt="Conveyr logo" style={{ maxWidth: "280px" }} />
              <img src={require("../assets/ribbon/loyalty.jpg")} alt="Conveyr logo" style={{ maxWidth: "280px" }} />
              <img src={require("../assets/ribbon/Offer1.png")} alt="Conveyr logo" style={{ maxWidth: "280px" }} />
              <img src={require("../assets/ribbon/Offer2.png")} alt="Conveyr logo" style={{ maxWidth: "280px" }} />
            </div>
          </div>

          
        </div>
      </div>

      <footer
  style={{
    padding: "2rem 1.5rem",
    textAlign: "center",
    background: "#fff",
    marginTop: "1rem",
    borderTop: "1px solid #e0e0e0",
  }}
>
  {/* COMPANY */}
  <p style={{ margin: "0 0 0.6rem 0", fontWeight: 600 }}>
    © 2025 Algebracorp OPC Private Limited
  </p>

  {/* ADDRESS */}
  <p style={{ margin: "0 0 1.2rem 0", fontSize: "0.9rem", color: "#555" }}>
    Office Address: 16th Park View, Gaur Yamuna City Greater Noida. Up
  </p>

  {/* SOCIAL ICONS */}
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: "1.4rem",
      marginBottom: "1rem",
    }}
  >
    {/* WhatsApp */}
    <a
      href="https://wa.me/+917518410292"
      target="_blank"
      rel="noopener noreferrer"
      title="WhatsApp"
      style={{ display: "inline-flex" }}
    >
      <img
        src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
        alt="WhatsApp"
        style={{
          height: "28px",
          width: "28px",
          transition: "transform 0.25s ease",
        }}
        onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.15)")}
        onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
      />
    </a>

    {/* Instagram */}
    <a
      href="https://www.instagram.com/algebracorp"
      target="_blank"
      rel="noopener noreferrer"
      title="Instagram"
      style={{ display: "inline-flex" }}
    >
      <img
        src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png"
        alt="Instagram"
        style={{
          height: "28px",
          width: "28px",
          transition: "transform 0.25s ease",
        }}
        onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.15)")}
        onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
      />
    </a>
  </div>

  {/* TAGLINE */}
  <p style={{ margin: 0, fontSize: "0.85rem", color: "#777" }}>
    Building India’s Next-Generation Digital & Commerce Ecosystems
  </p>
</footer>

    </div>
  );
}
